import Box from "@mui/material/Box";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useCallback, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import FormattedDate from "../../../components/FormattedDate.tsx";
import { keys } from "../../../api/queryKeys.ts";
import { api, type AiGenerationLogItem } from "../../../api/client.ts";
import FilterBar from "../../../components/filters/FilterBar.tsx";
import type { FilterSpec } from "../../../components/filters/specs.ts";
import { type FilterState, useFilters, useLocalFilterState } from "../../../components/filters/useFilters.ts";
import EmptyTableRow from "../../../components/table/EmptyTableRow.tsx";
import ScrollTable from "../../../components/table/ScrollTable.tsx";
import TableCard from "../../../components/table/TableCard.tsx";
import StatusChip from "../../../components/StatusChip.tsx";
import { formatNumber } from "../../../utils/format.ts";
import { aiMethodStatus } from "../../../utils/statusColors.ts";
import PageHeader from "../../../components/PageHeader.tsx";

function MetadataCell({ item, expanded, onToggle }: {
  item: AiGenerationLogItem;
  expanded: boolean;
  onToggle: () => void;
}) {
  const hasMetadata = Object.keys(item.metadata).length > 0;
  if (!hasMetadata) return <TableCell sx={{ color: "text.disabled" }}>—</TableCell>;
  return (
    <TableCell
      onClick={(e) => { e.stopPropagation(); onToggle(); }}
      sx={{ cursor: "pointer", maxWidth: 300 }}
    >
      {expanded ? (
        <Box
          component="pre"
          sx={{ m: 0, fontSize: "0.7rem", whiteSpace: "pre-wrap", wordBreak: "break-all" }}
        >
          {JSON.stringify(item.metadata, null, 2)}
        </Box>
      ) : (
        <Typography
          variant="body2"
          sx={{ fontFamily: "monospace", fontSize: "0.75rem", color: "text.secondary", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 280 }}
        >
          {JSON.stringify(item.metadata)}
        </Typography>
      )}
    </TableCell>
  );
}

// "Failures only" is in force until switched off, so "Clear all" comes back to it.
const FILTER_SPECS: FilterSpec[] = [
  {
    kind: "options",
    key: "failures",
    label: "Show",
    param: "failures",
    multiple: false,
    pinned: true,
    options: [{ value: "yes", label: "Failures only" }],
    default: ["yes"],
    noneValue: "all",
    noneLabel: "All attempts",
  },
  { kind: "text", key: "table_name", label: "Table name", param: "table_name", pinned: true, placeholder: "e.g. master_companies" },
  { kind: "text", key: "field_group", label: "Field group", param: "field_group", pinned: true, placeholder: "e.g. classification" },
];

function AiGenerationLogsPage() {
  const [page, setPage] = useState(0);
  const rowsPerPage = 50;
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Every filter change goes back to the first page.
  const local = useLocalFilterState();
  const localChange = local.changeFilter;
  const changeFilter = useCallback<FilterState["changeFilter"]>(
    (updates, replace) => {
      localChange(updates, replace);
      setPage(0);
      setExpandedId(null);
    },
    [localChange],
  );
  const filterState = useMemo(() => ({ searchParams: local.searchParams, changeFilter }), [local.searchParams, changeFilter]);
  const filters = useFilters(FILTER_SPECS, { state: filterState });
  const failuresOnly = filters.get("failures") !== "all";
  const tableName = filters.get("table_name") ?? "";
  const fieldGroup = filters.get("field_group") ?? "";

  const params = {
    failures_only: failuresOnly,
    table_name: tableName || undefined,
    field_group: fieldGroup || undefined,
    limit: rowsPerPage,
    offset: page * rowsPerPage,
  };

  const { data, isLoading } = useQuery({
    queryKey: keys.aiGenerationLog.list(params),
    queryFn: () => api.getAiGenerationLogs(params),
  });

  return (
    <>
      <PageHeader
        title="AI logs"
        description={
          <>
            Inspect AI generation attempts. Failures use method names prefixed with <code>skipped_</code>.
            Click a metadata cell to expand it.
          </>
        }
      />

      <TableCard
        loading={isLoading}
        toolbar={
          <FilterBar
            filters={filters}
            count={data && `${formatNumber(data.total)} ${data.total === 1 ? "record" : "records"}`}
          />
        }
        footer={
          <TablePagination
            component="div"
            count={data?.total ?? 0}
            page={page}
            onPageChange={(_, p) => { setPage(p); setExpandedId(null); }}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[50]}
            sx={{ borderTop: 1, borderColor: "divider" }}
          />
        }
      >
        <ScrollTable>
          <TableHead>
            <TableRow>
              <TableCell>Table</TableCell>
              <TableCell>Record ID</TableCell>
              <TableCell>Method</TableCell>
              <TableCell>Field group</TableCell>
              <TableCell>Model</TableCell>
              <TableCell>Generated</TableCell>
              <TableCell>Metadata</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(data?.items ?? []).map((item) => (
              <TableRow key={item.id} hover>
                <TableCell>
                  <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                    {item.table_name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Tooltip title={item.record_id}>
                    <Typography
                      variant="body2"
                      component="span"
                      sx={{ fontFamily: "monospace", fontSize: "0.75rem", color: "text.secondary" }}
                    >
                      {item.record_id.slice(0, 8)}…
                    </Typography>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <StatusChip kind="ai_method" value={aiMethodStatus(item.method)} label={item.method} />
                </TableCell>
                <TableCell>{item.field_group}</TableCell>
                <TableCell>
                  <Tooltip title={item.model}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: "monospace",
                        fontSize: "0.75rem",
                        maxWidth: 200,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {item.model}
                    </Typography>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  <FormattedDate value={item.generated_at} format="datetime" />
                </TableCell>
                <MetadataCell
                  item={item}
                  expanded={expandedId === item.id}
                  onToggle={() => setExpandedId(expandedId === item.id ? null : item.id)}
                />
              </TableRow>
            ))}
            {data?.items.length === 0 && (
              <EmptyTableRow colSpan={7}>
                {filters.activeCount > 0 || failuresOnly ? "No records match your filters" : "No records yet"}
              </EmptyTableRow>
            )}
          </TableBody>
        </ScrollTable>
      </TableCard>
    </>
  );
}

export default AiGenerationLogsPage;
